import { cn } from "@/lib/utils";
import type { ProcedureType } from "@/lib/specialtyTags";
import { useSpecialties } from "@/hooks/useSpecialties";
import { displaySpecialtyName } from "@/intake-v2/specialtyDisplayName";
import { useLingui } from "@lingui/react";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trans } from "@lingui/macro";

interface SpecialtySelectProps {
  value?: string; // slug string
  onChange?: (slug: string) => void;
  email?: string;
  disabled?: boolean;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const isIOSDevice =
  typeof navigator !== "undefined" &&
  (/(iPad|iPhone|iPod)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" &&
      (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints! >
        1));

export function SpecialtySelect({
  value,
  onChange,
  email,
  disabled = false,
  className,
  ...ariaProps
}: SpecialtySelectProps) {
  const { i18n } = useLingui();

  // Pass email and isDev=true as requested
  const { specialties, isLoading, error } = useSpecialties(email, true);

  const specialtyLabel = (s: (typeof specialties)[number]) =>
    displaySpecialtyName(s.slug, i18n, s.name);

  const handleSelect = (specialty: ProcedureType) => {
    if (!onChange || disabled) return;
    onChange(specialty.slug);
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center p-8', className)} {...ariaProps}>
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        <span className="ml-2 text-sm text-gray-600">Loading specialties...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('p-4 bg-red-50 border border-red-200 rounded-md', className)} {...ariaProps}>
        <p className="text-sm text-red-800">
          Failed to load specialties. Please refresh the page.
        </p>
      </div>
    );
  }

  if (!specialties || specialties.length === 0) {
    return (
      <div className={cn('p-4 bg-gray-50 border border-gray-200 rounded-md', className)} {...ariaProps}>
        <p className="text-sm text-gray-600">
          No specialties available at this time.
        </p>
      </div>
    );
  }

  const selectedSpecialty = value
    ? specialties.find((s) => s.slug === value)
    : undefined;

  const renderNativeSelect = () => (
    <select
      value={value ?? ""}
      onChange={(e) => {
        const slug = e.target.value;
        const specialty = specialties.find((s) => s.slug === slug);
        if (!specialty) return;
        if (specialty.availability?.isAvailable === false) {
          return;
        }
        handleSelect(specialty);
      }}
      disabled={disabled}
      className={cn(
        "h-12 w-full rounded-lg border-2 bg-white px-3 text-base focus:outline-none focus:ring-4 focus:ring-[#166B93]/10",
        disabled ? "opacity-60 cursor-not-allowed" : "border-gray-200",
      )}
      {...ariaProps}
    >
      <option value="">
        <Trans>Select a procedure</Trans>
      </option>
      {specialties.map((specialty) => {
        const isAvailable = specialty.availability?.isAvailable ?? true;
        const expiresAt = specialty.availability?.expiresAt;
        const availableDate = expiresAt
          ? new Date(expiresAt).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : null;

        const name = specialtyLabel(specialty);
        const label = isAvailable
          ? name
          : availableDate
            ? `${name} — Unavailable until ${availableDate}`
            : `${name} — Currently unavailable`;

        return (
          <option
            key={specialty.id}
            value={specialty.slug}
            disabled={!isAvailable}
          >
            {label}
          </option>
        );
      })}
    </select>
  );

  const renderRadixSelect = () => (
    <Select
      value={value ?? ""}
      onValueChange={(slug) => {
        const specialty = specialties.find((s) => s.slug === slug);
        if (!specialty) return;
        if (specialty.availability?.isAvailable === false) {
          return;
        }
        handleSelect(specialty);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        className={cn(
          "h-12 text-base border-2 rounded-lg w-full justify-between",
          disabled
            ? "opacity-60 cursor-not-allowed border-gray-200"
            : "border-gray-200 focus:border-[#166B93]",
        )}
        {...ariaProps}
      >
        <SelectValue placeholder={<Trans>Select a procedure</Trans>} />
      </SelectTrigger>
      <SelectContent
        position="popper"
        sideOffset={4}
        className="bg-white border-gray-200 shadow-lg rounded-xl max-h-[260px]"
      >
        {specialties.map((specialty) => {
          const isAvailable = specialty.availability?.isAvailable ?? true;
          const expiresAt = specialty.availability?.expiresAt;
          const availableDate = expiresAt
            ? new Date(expiresAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : null;

          return (
            <SelectItem
              key={specialty.id}
              value={specialty.slug}
              disabled={!isAvailable}
              className={cn(
                "flex items-center gap-2 py-2 px-3 text-sm rounded-lg",
                !isAvailable && "opacity-60 cursor-not-allowed",
              )}
            >
              <span className="flex-1 truncate">
                {specialtyLabel(specialty)}
              </span>
              {!isAvailable && (
                <span className="ml-2 text-[11px] font-medium text-gray-500">
                  {availableDate ? `Until ${availableDate}` : "Unavailable"}
                </span>
              )}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );

  return (
    <div className={cn("space-y-2", className)}>
      {isIOSDevice ? renderNativeSelect() : renderRadixSelect()}

      {selectedSpecialty && (
        <div className="mt-2 p-2 sm:p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-md">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-primary rounded-full flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium text-brand-primary flex items-center gap-2">
              <Trans>Selected:</Trans>
              <span>{specialtyLabel(selectedSpecialty)}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
